/**
 * CategoriesPage - Category Management
 * 
 * Displays and manages hierarchical categories with up to 3 levels.
 * 
 * @validates Requirements 5.1-5.6 - Category management
 */

import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCategoryStore } from '../store/categoryStore';
import { useAvailableTemplatesStore } from '../store/availableTemplatesStore';
import { CategoryTreeNode, CategoryBaseType, CreateCategoryRequest } from '@budgira/shared';
import EmojiPicker from '../components/EmojiPicker';
import AvailableTemplatesPanel from '../components/AvailableTemplatesPanel';

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const RemoveIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

// Icône pour "Tout déployer" - flèches qui s'écartent
const ExpandAllIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
  </svg>
);

// Icône pour "Tout masquer" - flèches qui pointent vers le centre (inverse de expand)
const CollapseAllIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V5m0 4H5m4 0L4 4m11 5V5m0 4h4m-4 0l5-5M9 15v4m0-4H5m4 0l-5 5m11-5v4m0-4h4m-4 0l5 5" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const FolderIcon = () => (
  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

interface CategoryNodeProps {
  node: CategoryTreeNode;
  level: number;
  onEdit: (category: CategoryTreeNode) => void;
  onDelete: (category: CategoryTreeNode) => void;
  onAddChild: (parent: CategoryTreeNode) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}

const CategoryNode = ({ node, level, onEdit, onDelete, onAddChild, expandedIds, onToggleExpand }: CategoryNodeProps) => {
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const canAddChild = node.hierarchyLevel < 3;
  const indent = level * 24;

  return (
    <div>
      <div 
        className="group flex items-center py-2 px-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors"
        style={{ paddingLeft: `${indent + 12}px` }}
      >
        {/* Expand/collapse button */}
        <button
          onClick={() => onToggleExpand(node.id)}
          className={`mr-2 p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors ${hasChildren ? 'visible' : 'invisible'}`}
        >
          {isExpanded ? (
            <ChevronDownIcon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          )}
        </button>

        {/* Logo/Icon */}
        <span className="mr-3 text-lg flex-shrink-0">
          {node.logoUrl || <FolderIcon />}
        </span>

        {/* Name */}
        <span className="flex-1 font-medium text-slate-700 dark:text-slate-200">
          {node.name}
        </span>

        {/* Type badge */}
        <span className={`text-xs px-2 py-0.5 rounded-full mr-3 ${
          node.baseType === 'credit' 
            ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300' 
            : 'bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300'
        }`}>
          {node.baseType === 'credit' ? 'Crédit' : 'Débit'}
        </span>

        {/* System badge */}
        {node.isSystem && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 mr-3">
            Système
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {canAddChild && (
            <button
              onClick={() => onAddChild(node)}
              className="p-1.5 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/50 text-slate-400 dark:text-slate-500 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              title="Ajouter une sous-catégorie"
            >
              <PlusIcon />
            </button>
          )}
          {!node.isSystem && (
            <>
              <button
                onClick={() => onEdit(node)}
                className="p-1.5 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/50 text-slate-400 dark:text-slate-500 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                title="Modifier"
              >
                <PencilIcon />
              </button>
              <button
                onClick={() => onDelete(node)}
                className="p-1.5 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/50 text-slate-400 dark:text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                title="Supprimer"
              >
                <TrashIcon />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children!.map((child) => (
            <CategoryNode
              key={child.id}
              node={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateCategoryRequest) => Promise<void>;
  editCategory?: CategoryTreeNode | null;
  parentCategory?: CategoryTreeNode | null;
}

const CategoryModal = ({ isOpen, onClose, onSave, editCategory, parentCategory }: CategoryModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [baseType, setBaseType] = useState<CategoryBaseType>('debit');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  // Determine if we're creating a root category or a subcategory
  const isRootCategory = !parentCategory && !editCategory?.parentId;
  const inheritedType = parentCategory?.baseType || editCategory?.baseType;

  useEffect(() => {
    if (editCategory) {
      setName(editCategory.name);
      setLogoUrl(editCategory.logoUrl || '');
      setBaseType(editCategory.baseType);
    } else if (parentCategory) {
      setName('');
      setLogoUrl('');
      setBaseType(parentCategory.baseType);
    } else {
      setName('');
      setLogoUrl('');
      setBaseType('debit');
    }
  }, [editCategory, parentCategory, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        logoUrl: logoUrl.trim() || undefined,
        baseType: isRootCategory ? baseType : undefined,
        parentId: parentCategory?.id,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {editCategory ? t('categories.modal.editTitle', 'Modifier la catégorie') : 
           parentCategory ? t('categories.modal.addSubTitle', 'Nouvelle sous-catégorie') :
           t('categories.modal.createTitle', 'Nouvelle catégorie')}
        </h2>

        {parentCategory && (
          <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('categories.modal.parentLabel', 'Catégorie parente :')}
            </p>
            <p className="font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <span>{parentCategory.logoUrl}</span>
              {parentCategory.name}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('categories.modal.name', 'Nom')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              placeholder={t('categories.modal.namePlaceholder', 'Ex: Alimentation, Transport...')}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('categories.modal.icon', 'Icône')}
            </label>
            <div className="relative">
              <button
                ref={emojiButtonRef}
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-left flex items-center gap-2"
              >
                {logoUrl ? (
                  <>
                    <span className="text-2xl">{logoUrl}</span>
                    <span className="text-slate-500 dark:text-slate-400">{t('categories.modal.changeIcon', 'Changer l\'icône')}</span>
                  </>
                ) : (
                  <span className="text-slate-400 dark:text-slate-500">{t('categories.modal.selectIcon', 'Choisir une icône...')}</span>
                )}
              </button>
              
              {showEmojiPicker && (
                <div className="absolute top-full left-0 mt-2 z-50">
                  <EmojiPicker
                    value={logoUrl}
                    onChange={(emoji) => setLogoUrl(emoji)}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                </div>
              )}
            </div>
          </div>

          {isRootCategory && !editCategory && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('categories.modal.type', 'Type')}
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="baseType"
                    value="debit"
                    checked={baseType === 'debit'}
                    onChange={() => setBaseType('debit')}
                    className="mr-2 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-slate-700 dark:text-slate-300">{t('categories.modal.debit', 'Débit (dépense)')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="baseType"
                    value="credit"
                    checked={baseType === 'credit'}
                    onChange={() => setBaseType('credit')}
                    className="mr-2 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-slate-700 dark:text-slate-300">{t('categories.modal.credit', 'Crédit (recette)')}</span>
                </label>
              </div>
            </div>
          )}

          {!isRootCategory && inheritedType && (
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('categories.modal.inheritedType', 'Type hérité :')}
                <span className={`ml-2 font-medium ${inheritedType === 'credit' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {inheritedType === 'credit' ? 'Crédit' : 'Débit'}
                </span>
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteModalProps {
  isOpen: boolean;
  category: CategoryTreeNode | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteModal = ({ isOpen, category, onClose, onConfirm }: DeleteModalProps) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen || !category) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('categories.deleteModal.title', 'Supprimer la catégorie')}
        </h2>
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          {t('categories.deleteModal.message', 'Êtes-vous sûr de vouloir supprimer la catégorie "{{name}}" ? Cette action est irréversible.', { name: category.name })}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 transition-colors"
          >
            {isDeleting ? t('common.loading') : t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function CategoriesPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const { 
    categories, 
    isLoading, 
    error, 
    fetchCategories, 
    createCategory, 
    updateCategory, 
    deleteCategory,
    addAllTemplates,
    removeTemplate,
    purgeUnused,
    clearError 
  } = useCategoryStore();

  const { 
    templates: availableTemplates, 
    fetchAvailableTemplates 
  } = useAvailableTemplatesStore();

  const [showModal, setShowModal] = useState(false);
  const [editCategory, setEditCategory] = useState<CategoryTreeNode | null>(null);
  const [parentCategory, setParentCategory] = useState<CategoryTreeNode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CategoryTreeNode | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showRemoveTemplateModal, setShowRemoveTemplateModal] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [isAddingAll, setIsAddingAll] = useState(false);
  const [isRemovingTemplate, setIsRemovingTemplate] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Helper to collect all category IDs recursively
  const getAllCategoryIds = (nodes: CategoryTreeNode[]): string[] => {
    const ids: string[] = [];
    const collect = (node: CategoryTreeNode) => {
      ids.push(node.id);
      node.children?.forEach(collect);
    };
    nodes.forEach(collect);
    return ids;
  };

  useEffect(() => {
    fetchCategories();
    fetchAvailableTemplates();
  }, [fetchCategories, fetchAvailableTemplates]);

  // Initialize expanded state when categories load - expand all by default
  useEffect(() => {
    if (categories.length > 0 && expandedIds.size === 0) {
      const allIds = getAllCategoryIds(categories);
      setExpandedIds(new Set(allIds));
    }
  }, [categories]);

  // Separate categories by type
  const debitCategories = categories.filter((c) => c.baseType === 'debit');
  const creditCategories = categories.filter((c) => c.baseType === 'credit');

  const handleToggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    const allIds = getAllCategoryIds(categories);
    setExpandedIds(new Set(allIds));
  };

  const handleCollapseAll = () => {
    setExpandedIds(new Set());
  };

  const handleAddRoot = () => {
    setEditCategory(null);
    setParentCategory(null);
    setShowModal(true);
  };

  const handleAddChild = (parent: CategoryTreeNode) => {
    setEditCategory(null);
    setParentCategory(parent);
    setShowModal(true);
  };

  const handleEdit = (category: CategoryTreeNode) => {
    setEditCategory(category);
    setParentCategory(null);
    setShowModal(true);
  };

  const handleDelete = (category: CategoryTreeNode) => {
    setDeleteTarget(category);
  };

  const handleSave = async (data: CreateCategoryRequest) => {
    if (editCategory) {
      await updateCategory(editCategory.id, {
        name: data.name,
        logoUrl: data.logoUrl,
      });
    } else {
      await createCategory(data);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      await deleteCategory(deleteTarget.id);
    }
  };

  const handleTemplateAdded = () => {
    fetchCategories();
    fetchAvailableTemplates();
  };

  const handleAddAllTemplates = async () => {
    setIsAddingAll(true);
    try {
      const count = await addAllTemplates();
      if (count > 0) {
        setSuccessMessage(t('categories.addAllSuccess', 'Catégories du template chargées : {{count}}', { count }));
        fetchAvailableTemplates();
        setTimeout(() => {
          const allIds = getAllCategoryIds(categories);
          setExpandedIds(new Set(allIds));
        }, 100);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } finally {
      setIsAddingAll(false);
    }
  };

  const handleRemoveTemplate = async () => {
    setIsRemovingTemplate(true);
    try {
      const count = await removeTemplate();
      if (count > 0) {
        setSuccessMessage(t('categories.removeTemplateSuccess', 'Catégories du template retirées : {{count}}', { count }));
        fetchAvailableTemplates();
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } finally {
      setIsRemovingTemplate(false);
      setShowRemoveTemplateModal(false);
    }
  };

  const handlePurgeUnused = async () => {
    setIsPurging(true);
    try {
      const count = await purgeUnused();
      if (count > 0) {
        setSuccessMessage(t('categories.purgeSuccess', 'Catégories personnelles non utilisées supprimées : {{count}}', { count }));
      } else {
        setSuccessMessage(t('categories.purgeNone', 'Aucune catégorie personnelle non utilisée à supprimer'));
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } finally {
      setIsPurging(false);
      setShowPurgeModal(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className={`${embedded ? 'text-xl' : 'text-2xl'} font-bold text-slate-900 dark:text-slate-100`}>{t('categories.title')}</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t('categories.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Template actions - only show if there are available templates */}
          {availableTemplates.length > 0 && (
            <button
              onClick={handleAddAllTemplates}
              disabled={isAddingAll || isLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('categories.addAllTemplates', 'Charger le template')}
            >
              <DownloadIcon />
              <span className="hidden md:inline">{t('categories.addAllTemplates', 'Charger le template')}</span>
            </button>
          )}
          {/* Remove template button */}
          <button
            onClick={() => setShowRemoveTemplateModal(true)}
            disabled={isRemovingTemplate || isLoading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={t('categories.removeTemplate', 'Retirer le template')}
          >
            <RemoveIcon />
            <span className="hidden md:inline">{t('categories.removeTemplate', 'Retirer le template')}</span>
          </button>
          {/* Purge unused button */}
          <button
            onClick={() => setShowPurgeModal(true)}
            disabled={isPurging || isLoading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={t('categories.purgeUnused', 'Épurer les non utilisées')}
          >
            <TrashIcon />
            <span className="hidden md:inline">{t('categories.purgeUnused', 'Épurer')}</span>
          </button>
          {/* Expand/Collapse toggle */}
          {categories.length > 0 && (
            <button
              onClick={expandedIds.size > 0 ? handleCollapseAll : handleExpandAll}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              title={expandedIds.size > 0 ? t('categories.collapseAll', 'Tout masquer') : t('categories.expandAll', 'Tout déployer')}
            >
              {expandedIds.size > 0 ? (
                <CollapseAllIcon className="w-5 h-5" />
              ) : (
                <ExpandAllIcon className="w-5 h-5" />
              )}
              <span className="hidden md:inline">{expandedIds.size > 0 ? t('categories.collapseAll', 'Tout masquer') : t('categories.expandAll', 'Tout déployer')}</span>
            </button>
          )}
          <button
            onClick={handleAddRoot}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <PlusIcon />
            <span className="hidden sm:inline">{t('categories.add', 'Nouvelle catégorie')}</span>
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-rose-700 dark:text-rose-300">{error}</p>
          <button
            onClick={clearError}
            className="text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && categories.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-violet-200 dark:border-violet-800 border-t-violet-600 dark:border-t-violet-400 rounded-full animate-spin" />
        </div>
      )}

      {/* Categories sections */}
      {!isLoading && categories.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
            <FolderIcon />
          </div>
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t('categories.empty.title', 'Aucune catégorie')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {t('categories.empty.message', 'Créez votre première catégorie pour organiser vos opérations.')}
          </p>
          <button
            onClick={handleAddRoot}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <PlusIcon />
            <span>{t('categories.empty.create', 'Créer une catégorie')}</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Debit categories */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-rose-50 dark:bg-rose-900/30 rounded-t-xl">
              <h2 className="font-semibold text-rose-700 dark:text-rose-300 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-rose-500" />
                {t('categories.debitTitle', 'Dépenses (Débit)')}
                <span className="text-sm font-normal text-rose-500 dark:text-rose-400">
                  ({debitCategories.length})
                </span>
              </h2>
            </div>
            <div className="py-2">
              {debitCategories.length === 0 ? (
                <p className="text-slate-400 dark:text-slate-500 text-center py-8">
                  {t('categories.noDebit', 'Aucune catégorie de dépense')}
                </p>
              ) : (
                debitCategories.map((category) => (
                  <CategoryNode
                    key={category.id}
                    node={category}
                    level={0}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAddChild={handleAddChild}
                    expandedIds={expandedIds}
                    onToggleExpand={handleToggleExpand}
                  />
                ))
              )}
            </div>
          </div>

          {/* Credit categories */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-emerald-50 dark:bg-emerald-900/30 rounded-t-xl">
              <h2 className="font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-emerald-500" />
                {t('categories.creditTitle', 'Revenus (Crédit)')}
                <span className="text-sm font-normal text-emerald-500 dark:text-emerald-400">
                  ({creditCategories.length})
                </span>
              </h2>
            </div>
            <div className="py-2">
              {creditCategories.length === 0 ? (
                <p className="text-slate-400 dark:text-slate-500 text-center py-8">
                  {t('categories.noCredit', 'Aucune catégorie de revenu')}
                </p>
              ) : (
                creditCategories.map((category) => (
                  <CategoryNode
                    key={category.id}
                    node={category}
                    level={0}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAddChild={handleAddChild}
                    expandedIds={expandedIds}
                    onToggleExpand={handleToggleExpand}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Available Templates Panel - only show if there are available templates */}
      {availableTemplates.length > 0 && (
        <AvailableTemplatesPanel onTemplateAdded={handleTemplateAdded} />
      )}

      {/* Modals */}
      <CategoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditCategory(null);
          setParentCategory(null);
        }}
        onSave={handleSave}
        editCategory={editCategory}
        parentCategory={parentCategory}
      />

      <DeleteModal
        isOpen={!!deleteTarget}
        category={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      {/* Remove Template Modal */}
      {showRemoveTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('categories.removeTemplateModal.title', 'Retirer le template')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              {t('categories.removeTemplateModal.message', 'Cette action supprimera toutes les catégories issues du template. Vos catégories personnalisées seront conservées. Les catégories associées à des opérations ne pourront pas être supprimées.')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRemoveTemplateModal(false)}
                className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleRemoveTemplate}
                disabled={isRemovingTemplate}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 transition-colors"
              >
                {isRemovingTemplate ? t('common.loading') : t('categories.removeTemplateModal.confirm', 'Retirer le template')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purge Unused Modal */}
      {showPurgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('categories.purgeModal.title', 'Épurer les catégories non utilisées')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              {t('categories.purgeModal.message', 'Cette action supprimera les catégories personnelles (non issues du template) qui ne sont associées à aucune opération ni échéancier.')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPurgeModal(false)}
                className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handlePurgeUnused}
                disabled={isPurging}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                {isPurging ? t('common.loading') : t('categories.purgeModal.confirm', 'Épurer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 bg-emerald-50 dark:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 shadow-lg z-50">
          <p className="text-emerald-700 dark:text-emerald-300">{successMessage}</p>
        </div>
      )}
    </div>
  );
}
